import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Mail, Users, Loader2 } from 'lucide-react';
import { useConference } from '@/context/ConferenceContext';
import { getMembersByUser, getErrorMessage } from '@/services/api';
import { toast } from 'sonner';

const CommitteePage = () => {
  const { shortName, importantDetails, loading: confLoading, siteConfig, loginDetails } = useConference();
  const [committee, setCommittee] = useState(() => {
    // Initialize with cached OCM members
    try {
      const cached = localStorage.getItem('conferenceSpeakers');
      if (cached) {
        const allMembers = JSON.parse(cached);
        return allMembers.filter(member => member.category?.toLowerCase() === 'ocm');
      }
    } catch {
      return [];
    }
    return [];
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCommittee = async () => {
      const username = loginDetails?.username || localStorage.getItem('conferenceUsername');
      if (!username) {
        try {
          const cachedLogin = localStorage.getItem('conferenceLoginDetails');
          if (cachedLogin) {
            const parsed = JSON.parse(cachedLogin);
            if (parsed?.username) {
              localStorage.setItem('conferenceUsername', parsed.username);
              await loadCommittee(parsed.username);
              return;
            }
          }
        } catch (e) {
          console.error('Failed to get cached username:', e);
        }
        return;
      }
      
      await loadCommittee(username);
    };

    const loadCommittee = async (username) => {
      setLoading(true);
      try {
        const data = await getMembersByUser(username);
        // Filter only OCM category members
        const ocmMembers = (data || []).filter(member => member.category?.toLowerCase() === 'ocm');
        setCommittee(ocmMembers);
      } catch (error) {
        console.error('Failed to fetch committee:', error);
        // Keep using cached data if fetch fails
      } finally {
        setLoading(false);
      }
    };

    fetchCommittee();
  }, [loginDetails?.username]);

  if (loading || confLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-sky-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading committee...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Organizing Committee - {importantDetails?.ConferenceTitle || siteConfig?.name || 'Conference'}</title>
        <meta name="description" content="Conference organizing committee members" />
      </Helmet>

      <div className="bg-white min-h-screen">
        {/* Hero */}
        <section className="hero-gradient py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Organizing Committee</h1>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Meet the dedicated team organizing this conference
              </p>
            </motion.div>
          </div>
        </section>

        {/* Committee Members */}
        <section className="section-spacing">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {committee.length > 0 ? (
                committee.map((member, index) => (
                  <motion.div
                    key={member.id || index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="card-hover bg-white p-6 rounded-2xl border-2 border-gray-100 shadow-sm"
                    data-testid={`committee-member-${index}`}
                  >
                    <div className="text-center mb-3">
                      {member.photo ? (
                        <img 
                          src={member.photo} 
                          alt={member.name} 
                          className="w-32 h-32 rounded-full object-cover mx-auto mb-4 shadow-md"
                        />
                      ) : (
                        <div className="w-32 h-32 bg-gradient-to-br from-green-600 to-emerald-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4 shadow-md">
                          {member.name?.charAt(0) || '?'}
                        </div>
                      )}
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{member.name}</h3>
                      <p className="text-sm text-gray-600 mb-2">{member.affiliation}</p>
                      {member.biography && (
                        <p className="text-base text-blue-600 font-extrabold mb-3 line-clamp-2">{member.biography}</p>
                      )}
                      <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-green-100 text-green-800">
                        Committee Member
                      </div>
                    </div>
                    {member.email && (
                      <a
                        href={`mailto:${member.email}`}
                        className="flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-green-600 mt-3"
                      >
                        <Mail size={16} />
                        <span className="truncate">{member.email}</span>
                      </a>
                    )}
                  </motion.div>
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <p className="text-gray-500 text-lg">No committee members found.</p>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CommitteePage;
